import { z } from 'zod';
export const MAX_PUBLISHED_ALTERNATIVES = 6;
export const statusSchema = z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']);
export const urlSchema = z.url().refine((v) => /^https?:\/\//i.test(v), 'Use an HTTP or HTTPS URL');
export const normalize = (value: string) =>
  value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
export const slugify = (value: string) => normalize(value).replaceAll(' ', '-');
export const scentProfileSchema = z
  .object({
    accords: z
      .array(
        z.object({
          name: z.string().trim().min(1).max(40),
          intensity: z.number().int().min(0).max(5).nullable(),
        }),
      )
      .max(6),
    projection: z.enum(['Soft', 'Moderate', 'Strong']).nullable(),
    longevity: z.enum(['Light', 'Moderate', 'Long-lasting', 'Very long-lasting']).nullable(),
    performanceNote: z.string().trim().max(800).optional(),
    basis: z.string().trim().max(2000),
    sources: z.array(urlSchema).max(12),
    reviewedAt: z.iso.datetime().nullable(),
  })
  .superRefine((v, c) => {
    if (new Set(v.accords.map((a) => normalize(a.name))).size !== v.accords.length)
      c.addIssue({ code: 'custom', message: 'Each accord must be unique', path: ['accords'] });
    if (v.accords.length || v.projection || v.longevity) {
      if (v.basis.length < 20 || !v.sources.length || !v.reviewedAt)
        c.addIssue({
          code: 'custom',
          message: 'Add the editorial basis, source URLs and review date for these estimates',
          path: ['basis'],
        });
    }
    if (v.reviewedAt && Date.parse(v.reviewedAt) > Date.now() + 60000)
      c.addIssue({
        code: 'custom',
        message: 'Review date cannot be in the future',
        path: ['reviewedAt'],
      });
  });
export type ScentProfile = z.infer<typeof scentProfileSchema>;
// ISO 3166-1 alpha-2 to the ISO 4217 currency a retailer in that destination normally prices in.
// Used to prefill the field, never to overwrite an editor's choice: a retailer may price in
// another currency than the country it ships to.
const countryCurrencyTable =
  'AD:EUR AE:AED AF:AFN AG:XCD AI:XCD AL:ALL AM:AMD AO:AOA AR:ARS AS:USD AT:EUR AU:AUD AW:AWG AX:EUR AZ:AZN BA:BAM BB:BBD BD:BDT BE:EUR BF:XOF BG:EUR BH:BHD BI:BIF BJ:XOF BL:EUR BM:BMD BN:BND BO:BOB BQ:USD BR:BRL BS:BSD BT:BTN BW:BWP BY:BYN BZ:BZD CA:CAD CC:AUD CD:CDF CF:XAF CG:XAF CH:CHF CI:XOF CK:NZD CL:CLP CM:XAF CN:CNY CO:COP CR:CRC CU:CUP CV:CVE CW:ANG CX:AUD CY:EUR CZ:CZK DE:EUR DJ:DJF DK:DKK DM:XCD DO:DOP DZ:DZD EC:USD EE:EUR EG:EGP EH:MAD ER:ERN ES:EUR ET:ETB FI:EUR FJ:FJD FK:FKP FM:USD FO:DKK FR:EUR GA:XAF GB:GBP GD:XCD GE:GEL GF:EUR GG:GBP GH:GHS GI:GIP GL:DKK GM:GMD GN:GNF GP:EUR GQ:XAF GR:EUR GT:GTQ GU:USD GW:XOF GY:GYD HK:HKD HN:HNL HR:EUR HT:HTG HU:HUF ID:IDR IE:EUR IL:ILS IM:GBP IN:INR IO:USD IQ:IQD IR:IRR IS:ISK IT:EUR JE:GBP JM:JMD JO:JOD JP:JPY KE:KES KG:KGS KH:KHR KI:AUD KM:KMF KN:XCD KR:KRW KW:KWD KY:KYD KZ:KZT LA:LAK LB:LBP LC:XCD LI:CHF LK:LKR LR:LRD LS:LSL LT:EUR LU:EUR LV:EUR LY:LYD MA:MAD MC:EUR MD:MDL ME:EUR MF:EUR MG:MGA MH:USD MK:MKD ML:XOF MM:MMK MN:MNT MO:MOP MP:USD MQ:EUR MR:MRU MS:XCD MT:EUR MU:MUR MV:MVR MW:MWK MX:MXN MY:MYR MZ:MZN NA:NAD NC:XPF NE:XOF NF:AUD NG:NGN NI:NIO NL:EUR NO:NOK NP:NPR NR:AUD NU:NZD NZ:NZD OM:OMR PA:PAB PE:PEN PF:XPF PG:PGK PH:PHP PK:PKR PL:PLN PM:EUR PN:NZD PR:USD PS:ILS PT:EUR PW:USD PY:PYG QA:QAR RE:EUR RO:RON RS:RSD RU:RUB RW:RWF SA:SAR SB:SBD SC:SCR SD:SDG SE:SEK SG:SGD SH:SHP SI:EUR SJ:NOK SK:EUR SL:SLE SM:EUR SN:XOF SO:SOS SR:SRD SS:SSP ST:STN SV:USD SX:ANG SY:SYP SZ:SZL TC:USD TD:XAF TG:XOF TH:THB TJ:TJS TK:NZD TL:USD TM:TMT TN:TND TO:TOP TR:TRY TT:TTD TV:AUD TW:TWD TZ:TZS UA:UAH UG:UGX UM:USD US:USD UY:UYU UZ:UZS VA:EUR VC:XCD VE:VES VG:USD VI:USD VN:VND VU:VUV WF:XPF WS:WST XK:EUR YE:YER YT:EUR ZA:ZAR ZM:ZMW';
export const countryCurrency: Record<string, string> = Object.fromEntries(
  countryCurrencyTable.split(' ').map((pair) => pair.split(':') as [string, string]),
);
export const currencyForCountry = (code?: string | null) =>
  code ? (countryCurrency[code.toUpperCase()] ?? '') : '';
export const brandSchema = z.object({
  name: z.string().trim().min(1).max(150),
  slug: z
    .string()
    .regex(/^[a-z0-9-]+$/)
    .max(180),
  aliases: z.array(z.string().trim().min(1).max(150)).max(50).default([]),
});
export const ethicalStatusSchema = z.enum(['UNKNOWN', 'YES', 'NO']);
export type EthicalStatus = z.infer<typeof ethicalStatusSchema>;
export const fragranceSchema = z.object({
  crueltyFree: ethicalStatusSchema.optional(),
  vegan: ethicalStatusSchema.optional(),
  gender: z.enum(['', 'male', 'female', 'unisex']).default(''),
  scentProfile: scentProfileSchema.nullable().optional(),
  brandId: z.string().nullable(),
  name: z.string().trim().min(1).max(200),
  slug: z
    .string()
    .regex(/^[a-z0-9-]+$/)
    .max(240),
  concentration: z.string().trim().max(100).default(''),
  isOriginal: z.boolean(),
  description: z.string().trim().max(10000),
  status: statusSchema,
  identityVerified: z.boolean(),
  verifiedAt: z.iso.datetime().nullable(),
  sources: z.array(urlSchema).max(50),
  rawNotes: z.string().max(20000).default(''),
  // Optional so a form that does not carry the notes leaves the recorded ones untouched.
  keyNotes: z
    .array(z.string().trim().min(1).max(40))
    .max(6)
    .refine((v) => new Set(v.map(normalize)).size === v.length, 'Each key note must be unique')
    .optional(),
  mediaId: z.string().nullable(),
  aliases: z.array(z.string().trim().min(1).max(200)).max(100),
});
export const relationshipSchema = z
  .object({
    originalId: z.string().min(1),
    dupeId: z.string().min(1),
    type: z.enum(['CLOSE_DUPE', 'INSPIRED_ALTERNATIVE', 'RELATED_COMPARISON']),
    status: statusSchema,
    notes: z.string().trim().max(10000),
    rawNotes: z.string().max(20000).default(''),
    recommended: z.boolean(),
    recommendationReason: z.string().trim().max(2000),
    sortOrder: z.number().int().min(0).max(10000),
    sources: z.array(urlSchema).max(50),
    verifiedAt: z.iso.datetime().nullable(),
  })
  .superRefine((v, c) => {
    if (v.originalId === v.dupeId)
      c.addIssue({
        code: 'custom',
        message: 'An original cannot be its own dupe',
        path: ['dupeId'],
      });
    if (v.recommended && !v.recommendationReason)
      c.addIssue({
        code: 'custom',
        message: 'Add a reason for this recommendation',
        path: ['recommendationReason'],
      });
  });
export const mediaSchema = z.object({
  alt: z.string().trim().min(3).max(300),
  sourceUrl: urlSchema,
  rightsBasis: z.string().trim().min(5).max(2000),
  approved: z.boolean(),
});
export const retailerSchema = z.object({
  name: z.string().trim().min(1).max(150),
  website: urlSchema,
});
// An official store of the fragrance's brand is by definition a verified retailer link, so marking a
// link official also verifies it, dated now when no check date is recorded. The database enforces the same rule.
export const officialStoreIsVerified = (value: unknown) =>
  value && typeof value === 'object' && (value as { officialStore?: unknown }).officialStore === true
    ? {
        ...value,
        verificationStatus: 'VERIFIED',
        lastCheckedAt: (value as { lastCheckedAt?: unknown }).lastCheckedAt || new Date().toISOString(),
      }
    : value;
const offerFields = z
  .object({
    officialStore: z.boolean().default(false),
    affiliate: z.boolean().default(false),
    hideFromOtherBuyingLinks: z.boolean().default(false),
    deliveryStatus: z.enum(['UNKNOWN', 'SUPPORTED', 'UNSUPPORTED']).default('UNKNOWN'),
    deliveryCheckedAt: z.iso.datetime().nullable().default(null),
    priceCheckedAt: z.iso.datetime().nullable().default(null),
    availability: z.enum(['UNKNOWN', 'IN_STOCK', 'OUT_OF_STOCK']).default('UNKNOWN'),
    fragranceId: z.string().min(1),
    retailerId: z.string().min(1),
    country: z
      .string()
      .regex(/^[A-Z]{2}$/)
      .nullable(),
    url: urlSchema,
    customTitle: z.string().trim().min(1).max(120).nullable().default(null),
    verificationStatus: z.enum(['VERIFIED', 'UNVERIFIED']).default('UNVERIFIED'),
    verificationNotes: z.string().trim().max(2000).default(''),
    price: z
      .string()
      .regex(/^\d{1,10}(\.\d{1,2})?$/)
      .nullable(),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .nullable(),
    bottleSizeMl: z.number().int().positive().max(10000).nullable(),
    promoCode: z.string().trim().min(1).max(80).nullable().default(null),
    discountType: z.enum(['PERCENTAGE', 'FIXED_AMOUNT']).nullable().default(null),
    discountValue: z
      .string()
      .regex(/^\d{1,10}(\.\d{1,2})?$/)
      .nullable()
      .default(null),
    lastCheckedAt: z.iso.datetime().nullable(),
    shippingEvidenceUrl: urlSchema.nullable(),
    status: statusSchema,
  })
  .superRefine((v, c) => {
    const promotionFields = [v.promoCode, v.discountType, v.discountValue];
    if (promotionFields.some((value) => value !== null) && promotionFields.some((value) => value === null))
      c.addIssue({
        code: 'custom',
        message: 'A discount needs a code, type and value',
        path: ['promoCode'],
      });
    if (v.discountType === 'PERCENTAGE' && Number(v.discountValue) > 100)
      c.addIssue({ code: 'custom', message: 'Percentage discount cannot exceed 100', path: ['discountValue'] });
    if (v.discountValue !== null && Number(v.discountValue) <= 0)
      c.addIssue({ code: 'custom', message: 'Discount must be greater than zero', path: ['discountValue'] });
    if (v.discountType === 'FIXED_AMOUNT' && !v.currency)
      c.addIssue({ code: 'custom', message: 'A flat discount needs a currency', path: ['currency'] });
    if (
      v.deliveryStatus === 'SUPPORTED' &&
      (!v.country || !v.shippingEvidenceUrl || !v.deliveryCheckedAt)
    )
      c.addIssue({
        code: 'custom',
        message:
          'Supported delivery needs a destination, shipping evidence and a delivery check date',
        path: ['deliveryStatus'],
      });
    for (const key of ['priceCheckedAt', 'deliveryCheckedAt'] as const)
      if (v[key] && Date.parse(v[key]) > Date.now())
        c.addIssue({ code: 'custom', message: 'Check dates cannot be in the future', path: [key] });
    if (v.price !== null && !v.currency)
      c.addIssue({
        code: 'custom',
        message: 'A price needs a destination country whose currency is known',
        path: ['country'],
      });
    if (v.verificationStatus === 'VERIFIED' && !v.lastCheckedAt)
      c.addIssue({
        code: 'custom',
        message: 'A verified link records the date it was checked',
        path: ['verificationStatus'],
      });
    if (v.lastCheckedAt && Date.parse(v.lastCheckedAt) > Date.now() + 60000)
      c.addIssue({
        code: 'custom',
        message: 'Verification cannot be in the future',
        path: ['lastCheckedAt'],
      });
  });
export const offerSchema = z.preprocess(officialStoreIsVerified, offerFields);
export type FragranceInput = z.infer<typeof fragranceSchema>;
export type RelationshipInput = z.infer<typeof relationshipSchema>;
